import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { trackPageView } from "@/lib/analytics";
import ConsentBanner from "@/components/ConsentBanner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CourseDetail from "@/pages/CourseDetail";
import Lesson from "@/pages/Lesson";
import Quiz from "@/pages/Quiz";
import Flashcards from "@/pages/Flashcards";
import Certificate from "@/pages/Certificate";
import Admin from "@/pages/Admin";
import PmGuide from "@/pages/PmGuide";
import Leaderboard from "@/pages/Leaderboard";
import SuperAdminTags from "@/pages/SuperAdminTags";
import SuperAdminPMs from "@/pages/SuperAdminPMs";
import SuperAdminPMDetail from "@/pages/SuperAdminPMDetail";
import PracticeOpsDashboard from "@/pages/PracticeOpsDashboard";
import PracticeOpsLibrary from "@/pages/PracticeOpsLibrary";
import PracticeOpsInstance from "@/pages/PracticeOpsInstance";
import PracticeOpsHistory from "@/pages/PracticeOpsHistory";
import PracticeOpsAdmin from "@/pages/PracticeOpsAdmin";
import Verify from "@/pages/Verify";
import Privacy from "@/pages/Privacy";

function Protected({ children, admin = false, practiceOps = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-muted">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (practiceOps && !user.practice_ops_access && !user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/courses/:slug" element={<Protected><CourseDetail /></Protected>} />
            <Route path="/courses/:slug/lessons/:order" element={<Protected><Lesson /></Protected>} />
            <Route path="/courses/:slug/quiz" element={<Protected><Quiz /></Protected>} />
            <Route path="/courses/:slug/flashcards" element={<Protected><Flashcards /></Protected>} />
            <Route path="/courses/:slug/certificate" element={<Protected><Certificate /></Protected>} />
            <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
            <Route path="/super/tags" element={<Protected><SuperAdminTags /></Protected>} />
            <Route path="/super/pms" element={<Protected><SuperAdminPMs /></Protected>} />
            <Route path="/super/pms/:practiceId" element={<Protected><SuperAdminPMDetail /></Protected>} />
            <Route path="/practice-ops" element={<Protected practiceOps><PracticeOpsDashboard /></Protected>} />
            <Route path="/practice-ops/library" element={<Protected practiceOps><PracticeOpsLibrary /></Protected>} />
            <Route path="/practice-ops/instances/:instanceId" element={<Protected practiceOps><PracticeOpsInstance /></Protected>} />
            <Route path="/practice-ops/history" element={<Protected practiceOps><PracticeOpsHistory /></Protected>} />
            <Route path="/practice-ops/admin" element={<Protected practiceOps admin><PracticeOpsAdmin /></Protected>} />
            <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
            <Route path="/admin/guide" element={<Protected admin><PmGuide /></Protected>} />
            <Route path="/verify/:certId" element={<Verify />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <ConsentBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

// Fires a page_view on every route change. GA drops the hit if consent is denied.
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

export default App;
